import { useState, useEffect } from 'react';
import { GitCompare, ArrowRight, Plus, Minus, Clock, User } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { formatDateTime } from '@/utils/date';

interface StudyPlanVersionDetail {
  id: string;
  study_plan_id: string;
  version_number: number;
  title: string;
  description: string | null;
  ects_credits: number;
  estimated_hours: number;
  learning_outcomes: {
    knowledge?: string[];
    skills?: string[];
    competence?: string[];
  };
  content_modules: any[] | null;
  assessment_criteria: string | null;
  metadata: any;
  approved_by: string;
  approved_at: string;
  approver_name: string;
  change_comment: string | null;
}

interface VersionCompareProps {
  studyPlanId: string;
  userId: string;
}

type DiffType = 'added' | 'removed' | 'changed' | 'unchanged';

interface DiffItem {
  field: string;
  label: string;
  type: DiffType;
  oldValue?: any;
  newValue?: any;
}

export function VersionCompare({ studyPlanId, userId }: VersionCompareProps) {
  const [versions, setVersions] = useState<{ version_id: string; version_number: number; title: string; approved_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [leftVersionId, setLeftVersionId] = useState<string>('');
  const [rightVersionId, setRightVersionId] = useState<string>('');
  const [leftVersion, setLeftVersion] = useState<StudyPlanVersionDetail | null>(null);
  const [rightVersion, setRightVersion] = useState<StudyPlanVersionDetail | null>(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [studyPlanId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/study-plans/versions/list', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          study_plan_id: studyPlanId,
        }),
      });

      if (!response.ok) throw new Error('Kunne ikke hente versjoner');

      const data = await response.json();
      if (data.success) {
        const sortedVersions = (data.versions || [])
          .filter((v: any) => !v.deleted_at)
          .sort((a: any, b: any) => b.version_number - a.version_number);
        setVersions(sortedVersions);

        // Pre-select the two most recent versions
        if (sortedVersions.length >= 2) {
          setLeftVersionId(sortedVersions[1].version_id);
          setRightVersionId(sortedVersions[0].version_id);
        } else if (sortedVersions.length === 1) {
          setRightVersionId(sortedVersions[0].version_id);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting');
    } finally {
      setLoading(false);
    }
  };

  const loadVersionDetails = async () => {
    if (!leftVersionId || !rightVersionId) return;
    if (leftVersionId === rightVersionId) {
      setError('Velg to forskjellige versjoner for sammenligning');
      return;
    }

    try {
      setComparing(true);
      setError(null);

      const [leftRes, rightRes] = await Promise.all([
        authenticatedFetch('/api/study-plans/versions/get', {
          method: 'POST',
          body: JSON.stringify({ user_id: userId, version_id: leftVersionId }),
        }),
        authenticatedFetch('/api/study-plans/versions/get', {
          method: 'POST',
          body: JSON.stringify({ user_id: userId, version_id: rightVersionId }),
        }),
      ]);

      if (!leftRes.ok || !rightRes.ok) {
        throw new Error('Kunne ikke hente versjonsdetaljer');
      }

      const leftData = await leftRes.json();
      const rightData = await rightRes.json();

      if (leftData.success && rightData.success) {
        setLeftVersion(leftData.version);
        setRightVersion(rightData.version);
      } else {
        throw new Error('Feil ved henting av versjonsdata');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved sammenligning');
    } finally {
      setComparing(false);
    }
  };

  const getDiffClass = (type: DiffType) => {
    switch (type) {
      case 'added': return 'bg-green-50 border-green-200';
      case 'removed': return 'bg-red-50 border-red-200';
      case 'changed': return 'bg-amber-50 border-amber-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getDiffIcon = (type: DiffType) => {
    switch (type) {
      case 'added': return <Plus size={14} className="text-green-600" />;
      case 'removed': return <Minus size={14} className="text-red-600" />;
      case 'changed': return <ArrowRight size={14} className="text-amber-600" />;
      default: return null;
    }
  };

  const compareValues = (oldVal: any, newVal: any): DiffType => {
    if (oldVal === undefined && newVal !== undefined) return 'added';
    if (oldVal !== undefined && newVal === undefined) return 'removed';
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) return 'changed';
    return 'unchanged';
  };

  const renderDiffs = (): DiffItem[] => {
    if (!leftVersion || !rightVersion) return [];

    const diffs: DiffItem[] = [];

    // Basic fields
    diffs.push({
      field: 'title',
      label: 'Tittel',
      type: compareValues(leftVersion.title, rightVersion.title),
      oldValue: leftVersion.title,
      newValue: rightVersion.title
    });

    diffs.push({
      field: 'description',
      label: 'Beskrivelse',
      type: compareValues(leftVersion.description, rightVersion.description),
      oldValue: leftVersion.description,
      newValue: rightVersion.description
    });

    diffs.push({
      field: 'ects_credits',
      label: 'Kompetansepoeng',
      type: compareValues(leftVersion.ects_credits, rightVersion.ects_credits),
      oldValue: leftVersion.ects_credits,
      newValue: rightVersion.ects_credits
    });

    diffs.push({
      field: 'estimated_hours',
      label: 'Timer',
      type: compareValues(leftVersion.estimated_hours, rightVersion.estimated_hours),
      oldValue: leftVersion.estimated_hours,
      newValue: rightVersion.estimated_hours
    });

    // Learning outcomes
    const loCategories = ['knowledge', 'skills', 'competence'] as const;
    const loLabels = { knowledge: 'Kunnskap', skills: 'Ferdigheter', competence: 'Generell kompetanse' };

    loCategories.forEach(cat => {
      const oldLo = leftVersion.learning_outcomes?.[cat] || [];
      const newLo = rightVersion.learning_outcomes?.[cat] || [];
      diffs.push({
        field: `learning_outcomes.${cat}`,
        label: `Læringsutbytte: ${loLabels[cat]}`,
        type: compareValues(oldLo, newLo),
        oldValue: oldLo,
        newValue: newLo
      });
    });

    // Assessment
    diffs.push({
      field: 'assessment_criteria',
      label: 'Vurderingskriterier',
      type: compareValues(leftVersion.assessment_criteria, rightVersion.assessment_criteria),
      oldValue: leftVersion.assessment_criteria,
      newValue: rightVersion.assessment_criteria
    });

    // Content modules
    diffs.push({
      field: 'content_modules',
      label: 'Innholdsmoduler',
      type: compareValues(leftVersion.content_modules, rightVersion.content_modules),
      oldValue: leftVersion.content_modules,
      newValue: rightVersion.content_modules
    });

    return diffs;
  };

  const renderValueDisplay = (value: any, isArray = false): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">Ikke satt</span>;
    }
    if (isArray && Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">Ingen</span>;
      }
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm">{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      return <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">{JSON.stringify(value, null, 2)}</pre>;
    }
    return <span>{String(value)}</span>;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-4">
          <GitCompare className="h-6 w-6 text-kf-primary" />
          <h2 className="text-xl font-semibold">Sammenlign versjoner</h2>
        </div>
        <p className="text-gray-500">Laster versjoner...</p>
      </div>
    );
  }

  if (versions.length < 2) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-4">
          <GitCompare className="h-6 w-6 text-kf-primary" />
          <h2 className="text-xl font-semibold">Sammenlign versjoner</h2>
        </div>
        <p className="text-gray-500">
          Det må finnes minst to godkjente versjoner for å kunne sammenligne.
        </p>
      </div>
    );
  }

  const diffs = renderDiffs();
  const changedCount = diffs.filter(d => d.type !== 'unchanged').length;

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center gap-3 mb-6">
        <GitCompare className="h-6 w-6 text-kf-primary" />
        <h2 className="text-xl font-semibold">Sammenlign versjoner</h2>
      </div>

      {/* Version selectors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Eldre versjon
          </label>
          <select
            value={leftVersionId}
            onChange={(e) => setLeftVersionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-kf-primary"
          >
            <option value="">Velg versjon...</option>
            {versions.map(v => (
              <option key={v.version_id} value={v.version_id}>
                Versjon {v.version_number} ({formatDateTime(v.approved_at)})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end justify-center">
          <ArrowRight className="text-gray-400" size={24} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nyere versjon
          </label>
          <select
            value={rightVersionId}
            onChange={(e) => setRightVersionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-kf-primary"
          >
            <option value="">Velg versjon...</option>
            {versions.map(v => (
              <option key={v.version_id} value={v.version_id}>
                Versjon {v.version_number} ({formatDateTime(v.approved_at)})
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={loadVersionDetails}
        disabled={!leftVersionId || !rightVersionId || comparing}
        className="w-full md:w-auto px-6 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {comparing ? 'Sammenligner...' : 'Sammenlign'}
      </button>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {/* Comparison results */}
      {leftVersion && rightVersion && (
        <div className="mt-6 space-y-4">
          {/* Summary */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-sm text-gray-500">Fra</div>
                  <div className="font-semibold">Versjon {leftVersion.version_number}</div>
                </div>
                <ArrowRight className="text-gray-400" />
                <div className="text-center">
                  <div className="text-sm text-gray-500">Til</div>
                  <div className="font-semibold">Versjon {rightVersion.version_number}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-kf-primary">{changedCount}</div>
                <div className="text-sm text-gray-500">endringer</div>
              </div>
            </div>
          </div>

          {/* Version metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded border">
              <div className="text-sm text-gray-500 mb-1">Versjon {leftVersion.version_number}</div>
              <div className="flex items-center gap-2 text-sm">
                <Clock size={14} className="text-gray-400" />
                <span>{formatDateTime(leftVersion.approved_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <User size={14} className="text-gray-400" />
                <span>{leftVersion.approver_name}</span>
              </div>
              {leftVersion.change_comment && (
                <p className="text-sm text-gray-600 mt-2 italic">"{leftVersion.change_comment}"</p>
              )}
            </div>
            <div className="p-3 bg-gray-50 rounded border">
              <div className="text-sm text-gray-500 mb-1">Versjon {rightVersion.version_number}</div>
              <div className="flex items-center gap-2 text-sm">
                <Clock size={14} className="text-gray-400" />
                <span>{formatDateTime(rightVersion.approved_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <User size={14} className="text-gray-400" />
                <span>{rightVersion.approver_name}</span>
              </div>
              {rightVersion.change_comment && (
                <p className="text-sm text-gray-600 mt-2 italic">"{rightVersion.change_comment}"</p>
              )}
            </div>
          </div>

          {/* Differences */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Detaljerte endringer</h3>

            {diffs.filter(d => d.type !== 'unchanged').length === 0 ? (
              <p className="text-gray-500 italic">Ingen endringer funnet mellom disse versjonene.</p>
            ) : (
              diffs.filter(d => d.type !== 'unchanged').map((diff, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${getDiffClass(diff.type)}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getDiffIcon(diff.type)}
                    <span className="font-medium">{diff.label}</span>
                    <span className="text-xs text-gray-500 uppercase">
                      {diff.type === 'added' && 'Lagt til'}
                      {diff.type === 'removed' && 'Fjernet'}
                      {diff.type === 'changed' && 'Endret'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    {diff.type !== 'added' && (
                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-gray-500 mb-1">Før (v{leftVersion.version_number})</div>
                        <div className="text-sm text-red-700">
                          {renderValueDisplay(diff.oldValue, Array.isArray(diff.oldValue))}
                        </div>
                      </div>
                    )}
                    {diff.type !== 'removed' && (
                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-gray-500 mb-1">Etter (v{rightVersion.version_number})</div>
                        <div className="text-sm text-green-700">
                          {renderValueDisplay(diff.newValue, Array.isArray(diff.newValue))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
